/**
 * Order + geometry for the footer "More" overflow.
 * Rationale: `docs/design-docs/sidebar-footer-rail-overflow.md`.
 */

/**
 * The footer collapsibles top-to-bottom as they render in the sidebar — the
 * single source of truth for ordering. Items collapse into "More" in this order
 * (top item, Refer, first) and the dropdown lists them in this order too. The
 * collapse deliberately follows the VISUAL structure, not product priority — a
 * designer decision; a deeper IA re-sort is acknowledged but out of scope here.
 */
export const FOOTER_RAIL_DISPLAY_ORDER = [
  'refer',
  'getStarted',
  'discovery',
  'evidence',
  'integrations',
  'remote',
  'dictate',
  'help',
  'notifications',
  'usage',
] as const;

export type FooterRailId = (typeof FOOTER_RAIL_DISPLAY_ORDER)[number];

/**
 * Collapse priority for `splitFooterRail`, which keeps the front and collapses
 * the tail first — so it is the display order reversed (the top-most visual item
 * collapses first). Remote's connected-device pin overrides this separately.
 */
export const FOOTER_RAIL_ORDER: readonly FooterRailId[] = [
  ...FOOTER_RAIL_DISPLAY_ORDER,
].reverse();

/**
 * Each collapsible item's `data-testid`(s), used to attribute measured heights.
 * Discovery has two variants (discovery-call vs new-user live-demo) that share
 * one slot, so it lists both.
 */
export const FOOTER_RAIL_TEST_IDS: Readonly<
  Record<FooterRailId, string | readonly string[]>
> = {
  dictate: 'sidebar-footer-dictation-logs',
  remote: 'sidebar-footer-devices',
  integrations: 'sidebar-link-integrations',
  evidence: 'sidebar-evidence-referral-cta',
  discovery: ['sidebar-link-discovery-call', 'sidebar-link-live-demo'],
  getStarted: 'sidebar-link-get-started-faq',
  refer: 'sidebar-referral-button',
  help: 'sidebar-footer-help',
  notifications: 'sidebar-footer-notifications',
  usage: 'side-nav-credit-usage-popover-button',
};

export const FOOTER_MORE_TEST_ID = 'sidebar-footer-more';

/**
 * Footer-rail ids that are only active under the sidebar IA flag.
 * On flag-off these tiles are pinned, not collapsible, and must not
 * be counted in renderedCollapsibleSum — otherwise the pinned reserve
 * is under-calculated and the non-IA collapsibles over-collapse.
 */
export const FOOTER_RAIL_IA_ONLY_IDS = new Set<FooterRailId>([
  'help',
  'notifications',
  'usage',
] as const satisfies FooterRailId[]);

/** Icon-only footer button floor (~36px); fallback for an id never yet measured. */
export const FOOTER_RAIL_TILE_ESTIMATE_PX = 36;

/** The footer column and its icon strip are `gap-2` (8px). */
export const FOOTER_RAIL_GAP_PX = 8;

/** Extra room to drop the More tile once shown, so it can't strobe on drag. */
export const FOOTER_RAIL_HYSTERESIS_PX = 6;
/** Exit duration of a folding footer tile — threads the one-at-a-time cascade. */
export const FOOTER_COLLAPSE_EXIT_MS = 180;
